import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';

// --- Mock Data Embedded ---
const mockAnomalies = [
  { date: '2023-07-15', percentage: 30, reason: 'HVAC malfunction' },
  { date: '2023-09-22', percentage: 25, reason: 'Equipment left on overnight' }
];

const mockExpensiveUseTimes = [
  { time: '2:00 PM - 6:00 PM', rate: 'Peak', cost: 0.28 },
  { time: '10:00 AM - 2:00 PM', rate: 'Mid-Peak', cost: 0.22 },
  { time: '6:00 PM - 10:00 PM', rate: 'Mid-Peak', cost: 0.22 },
  { time: '10:00 PM - 10:00 AM', rate: 'Off-Peak', cost: 0.16 }
];

const mockBillForecast = {
  currentBill: 756,
  forecastNextMonth: 774,
  bestCase: 720,
  worstCase: 810
};

// Example base usage for calculation - replace with better logic if needed
const exampleMonthlyKwh = 4000;
const exampleMonthlyCost = 720;
// --- End Embedded Mock Data ---

const AnomaliesAlerts = () => {
  const theme = useTheme();

  // Use embedded mock data
  const anomalies = mockAnomalies;
  const expensiveUseTimes = mockExpensiveUseTimes;
  const billForecast = mockBillForecast;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Energy Anomalies & Alerts
      </Typography>

      {anomalies && anomalies.length > 0 ? (
        <Grid container spacing={3}>
          {anomalies.map((anomaly, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                    <WarningAmberIcon sx={{ mr: 1, fontSize: '1.2rem', color: theme.palette.error.main }} />
                    Energy Anomaly Detected
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 0.5, fontSize: '0.8rem' }} />
                    {new Date(anomaly.date).toLocaleDateString()}
                  </Typography>
                </Box>

                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                        {anomaly.percentage}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Increase over normal
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {anomaly.reason}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body2">
                    {/* Simplified calculation using example data */}
                    This anomaly resulted in approximately {Math.round(exampleMonthlyKwh * (anomaly.percentage / 100))} kWh of unexpected energy consumption, costing an estimated ${Math.round(exampleMonthlyCost * (anomaly.percentage / 100))}.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                <FindReplaceIcon sx={{ mr: 1, verticalAlign: 'bottom', color: theme.palette.primary.main }} />
                Mitigation Suggestions
              </Typography>

              <Typography variant="body2" component="div">
                Based on the detected anomalies, consider implementing the following:
                <ul>
                  <li>Regular maintenance checks for HVAC systems</li>
                  <li>Automatic shut-off systems for equipment during non-operational hours</li>
                  <li>Real-time energy monitoring alerts to catch issues quickly</li>
                  <li>Staff training on energy-saving protocols</li>
                </ul>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', border: `1px dashed ${theme.palette.divider}`, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            No Anomalies Detected Recently
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your energy usage patterns appear consistent.
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Cost Optimization Opportunities
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Time-of-Use Rates</Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={expensiveUseTimes || []}
                margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                <YAxis
                  label={{
                    value: 'Cost ($ per kWh)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle' },
                    dy: -10 // Adjust label position if needed
                  }}
                  tickFormatter={(value) => `$${value.toFixed(2)}`}
                  domain={[0, 'auto']}
                  width={80} // Increase width for labels
                />
                <ChartTooltip formatter={(value: number) => [`$${value.toFixed(2)} per kWh`, 'Energy Rate']} />
                <Bar
                  dataKey="cost"
                  fill={theme.palette.warning.main}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
           <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Bill Forecast Summary</Typography>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              height: 'calc(100% - 30px)', // Adjust height to align better
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Current Bill</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${billForecast?.currentBill ?? 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Forecast (Next Month)</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${billForecast?.forecastNextMonth ?? 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Potential Savings</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                    ${(billForecast?.forecastNextMonth ?? 0) - (billForecast?.bestCase ?? 0)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  Shifting energy use away from peak hours (e.g., 2PM-6PM) could reduce your monthly bill by up to **${(billForecast?.worstCase ?? 0) - (billForecast?.bestCase ?? 0)}**.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnomaliesAlerts; 